import React, {
  JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import _ from 'lodash';
import {
  ArrowLeft,
  ArrowRight,
  BadgeDollarSignIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CircleCheckIcon,
  CoinsIcon,
  InfoIcon,
  Loader2,
  MapPin,
  Navigation,
  ShieldCheckIcon,
  Truck,
} from 'lucide-react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/Collapsible';
import KitDefaultValueTooltip from 'components/Kit/KitDefaultValueTooltip';
import QuoteConfidenceLevel from 'components/QuoteConfidenceLevel';
import { Input } from 'components/input';
import { useIsTqlInbox } from 'hooks/useIsTqlInbox';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { toast } from 'hooks/useToaster';
import { getDATMarketInformation } from 'lib/api/getDATMarketInformation';
import {
  KencoFlashQuoteMetadata,
  QuickQuoteResponseStop,
  RouteSegment,
  SonarQuoteMetadata,
  SuperDispatchQuoteMetadata,
} from 'lib/api/getQuickQuote';
import { updateUserDefaultStopFee } from 'lib/api/updateUserDefaultStopFee';
import { useQuickQuoteCurrency } from 'pages/QuoteView/Quoting/RequestQuickQuote/hooks/useQuickQuoteCurrency';
import {
  DATQuoteLocationType,
  DATQuoteTimeframe,
  LaneTier,
  QuoteFormStop,
  QuoteTypeInSource,
  SelectedQuoteType,
  getEquipmentDisplayLabel,
  getSubTransportTypeOptions,
  laneTierMap,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { TransportType } from 'types/QuoteRequest';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { Quoting } from 'types/enums/Integrations';
import { TruPayQuoteMetadata } from 'types/trupayQuote';
import { formatCurrency } from 'utils/formatCurrency';
import { titleCase } from 'utils/formatStrings';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import { cn } from 'utils/shadcn';

import { Button } from './Button';
import { DATAreaTypeSlider } from './DATAreaTypeSlider';
import { DATTimeframeSlider } from './DATTimeframeSlider';
import { QuoteCardKenco } from './QuoteCardKenco';
import { QuoteCardSonar } from './QuoteCardSonar';
import { QuoteCardSuperDispatch } from './QuoteCardSuperDispatch';
import { QuoteCardTransfix } from './QuoteCardTransfix';
import { QuoteCardTruPay } from './QuoteCardTruPay';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './Tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip';
import { Flex, Grid } from './layout';
import { Typography } from './typography';

// TODO: Deduplicate with Metadata
export type TooltipContentType = {
  typeInSource: QuoteTypeInSource;
  isMultiStop: boolean;
  stops: QuoteFormStop[];
  routeSegments?: RouteSegment[];
  // GS specific
  isZipCodeLookup?: boolean;
  // DAT specific
  timeframe?: string;
  originName?: string;
  originType?: DATQuoteLocationType | LaneTier;
  destinationName?: string;
  destinationType?: DATQuoteLocationType | LaneTier;
  reports?: number;
  companies?: number;
  stopFeeUSDMedium?: number;
  datMileage?: number;
  pcmilerMileage?: number;
};

export type PriceRangeType = {
  lowEstimate: number;
  highEstimate: number;
};

export type DATMarketInformation = {
  stopLocation: string; // e.g. "Chicago, IL"
  inboundLoadToTruckRatio: number;
  inboundMarketConditionsIndexPrevDay: number;
  inboundMarketConditionsIndexPrevWeek: number;
  inboundMarketConditionsForecastNextDay: number;
  outboundLoadToTruckRatio: number;
  outboundMarketConditionsIndexPrevDay: number;
  outboundMarketConditionsIndexPrevWeek: number;
  outboundMarketConditionsForecastNextDay: number;
};

export type QuoteCardType = {
  type: SelectedQuoteType; // e.g. NETWORK, BUYPOWER, DAT
  // type: QuoteTypeInSource;
  title: string; // e.g. "Network Quote"
  subtitle?: string; // e.g. "Longest Leg"
  icon: React.ReactNode; // e.g. <GreenscreensLogo />
  cost: number; // e.g. 1125 (flat number, not a function)
  priceRange: Maybe<PriceRangeType>; // e.g. $1125 - $1350
  confidence: Maybe<number>; // e.g. 85 (confidence out of 100)
  costPerMile?: number; // e.g. 2.10 (flat number, not a function)
  priceRangePerMile?: Maybe<PriceRangeType>; // e.g. $2.05 - $2.15
  inputtedTransportType: TransportType; // Greenscreens does not support some enums like HOTSHOT and BOXTRUCK,
  actualTransportType: TransportType; // so the data is proxied with other types. We want to inform the user which data source was used
  subTransportType?: string; // granular equipment (e.g. "SPRINTER VAN") when enabled; drives the Equipment label on the card
  quotingIntegrationName?: string; // e.g. "Greenscreens", "DAT", "Truckstop"

  // Additional props needed for fetching market conditions
  quoteId?: number; // QuickQuote ID from the backend response
  stops: QuickQuoteResponseStop[];
  isMultiStop?: boolean; // Flag to identify multi-stop quotes
  legs?: RouteSegment[]; // Individual leg data for multi-stop quotes
  kencoMetadata?: Partial<KencoFlashQuoteMetadata>;
  sonarMetadata?: Partial<SonarQuoteMetadata>;
  superDispatchMetadata?: Partial<SuperDispatchQuoteMetadata>;
  trupayMetadata?: TruPayQuoteMetadata;
} & (
  | {
      tooltipContent: MaybeUndef<TooltipContentType>;
      tooltipConstructor: (content: TooltipContentType) => JSX.Element; // e.g. DATTooltipConstructor
    }
  | {
      tooltipContent?: undefined;
      tooltipConstructor?: undefined;
    }
);

type QuoteCardProps = {
  carrier: QuoteCardType;
  isSelected: boolean;
  onClick: () => void;
  lowConfidenceThreshold: number;
  mediumConfidenceThreshold: number;
  onUpdateRateView?: (
    timeframe: DATQuoteTimeframe,
    areaType: DATQuoteLocationType,
    setUpdateRateViewError: (error: Maybe<string>) => void
  ) => void;
  stops: QuickQuoteResponseStop[];
  stopFee?: number;
  setStopFee?: (fee: number) => void;
  intermediateStopCount?: number;
};

export const QuoteCard = ({
  carrier,
  isSelected,
  onClick,
  lowConfidenceThreshold,
  mediumConfidenceThreshold,
  onUpdateRateView,
  stops,
  stopFee,
  setStopFee,
  intermediateStopCount = 0,
}: QuoteCardProps) => {
  const isTql = useIsTqlInbox();

  const [expandedTargetBuy, setExpandedTargetBuy] = useState(false);
  const [expandedPriceRange, setExpandedPriceRange] = useState(false);

  const {
    serviceFeaturesEnabled: { isUpdateRateViewEnabled },
  } = useServiceFeatures();
  const {
    isEnabled: isQuoteCurrencyEnabled,
    formatAmount,
    formatRange,
    formatPerMileRate,
  } = useQuickQuoteCurrency();

  const formatBuyRate = (amount: number, maxFractionDigits = 0) => {
    if (!isQuoteCurrencyEnabled) {
      return `$${amount}`;
    }

    return formatAmount(amount, maxFractionDigits);
  };

  const formatStopFeeAmount = (amount: number) => {
    if (!isQuoteCurrencyEnabled) {
      return formatCurrency(amount, 'USD', 0);
    }

    return formatAmount(amount, 0);
  };

  const roundedCost = _.round(carrier.cost);
  const roundedCostPerMile = carrier.costPerMile
    ? _.round(carrier.costPerMile, 2)
    : null;

  const [isMarketInformationExpanded, setIsMarketInformationExpanded] =
    useState(true);
  const [isMarketInformationLoading, setIsMarketInformationLoading] =
    useState(false);
  const [marketInformationData, setMarketInformationData] = useState<
    Maybe<{
      pickupMarketInformation?: DATMarketInformation;
      dropoffMarketInformation?: DATMarketInformation;
    }>
  >();
  // Track whether we've attempted to fetch with the current set of dependencies
  const hasAttemptedFetchRef = useRef(false);
  // Track abort controller to cancel in-flight requests
  const abortControllerRef = useRef<Maybe<AbortController>>(null);

  const {
    serviceFeaturesEnabled: {
      isDATMarketConditionsEnabled,
      isDATMarketConditionsForecastEnabled,
      isDATLoadToTruckRatioEnabled,
    },
    quotingIntegrations,
    subTransportTypes,
  } = useServiceFeatures();

  // The card shows the submitted (parent) transport type as the value, and - reusing the
  // existing proxy-tooltip - explains the mapping. When a granular equipment type was selected
  // (e.g. "Flatbed Conestoga"), that granular label is the "inputted" side of the tooltip;
  // otherwise it falls back to the parent inputted type (e.g. Box Truck -> Van proxying).
  const subTransportTypeOptions = getSubTransportTypeOptions(subTransportTypes);
  const submittedLabel = titleCase(carrier.actualTransportType);
  const inputtedEquipmentLabel = carrier.subTransportType
    ? getEquipmentDisplayLabel(
        carrier.subTransportType,
        carrier.inputtedTransportType,
        subTransportTypeOptions
      )
    : titleCase(carrier.inputtedTransportType);
  const showEquivalenceTooltip =
    (Boolean(carrier.subTransportType) &&
      inputtedEquipmentLabel !== submittedLabel) ||
    carrier.inputtedTransportType !== carrier.actualTransportType;

  const roundedPriceRange = carrier.priceRange
    ? {
        lowEstimate: _.round(carrier.priceRange.lowEstimate),
        highEstimate: _.round(carrier.priceRange.highEstimate),
      }
    : null;

  const roundedPriceRangePerMile = carrier.priceRangePerMile
    ? {
        lowEstimate: _.round(carrier.priceRangePerMile.lowEstimate, 2),
        highEstimate: _.round(carrier.priceRangePerMile.highEstimate, 2),
      }
    : null;

  const roundedConfidence = carrier.confidence
    ? _.round(carrier.confidence)
    : null;

  const isDATRateView = carrier.type === SelectedQuoteType.DAT_RATEVIEW;
  const isDATLongestLeg = carrier.type === SelectedQuoteType.DAT_LONGEST_LEG;
  const [updateRateViewError, setUpdateRateViewError] =
    useState<Maybe<string>>(null);
  const [stopFeeInputValue, setStopFeeInputValue] = useState<string>(
    String(stopFee ?? 0)
  );
  const [defaultStopFee, setDefaultStopFee] = useState(stopFee ?? 0);
  const [isDefaultStopFee, setIsDefaultStopFee] = useState(true);

  const showStopFeeEditor =
    isSelected &&
    isDATLongestLeg &&
    intermediateStopCount > 0 &&
    typeof setStopFee === 'function';

  const stopFeeValue = Number.isNaN(parseInt(stopFeeInputValue, 10))
    ? 0
    : parseInt(stopFeeInputValue, 10);
  const stopFeeTotal = stopFeeValue * intermediateStopCount;
  const intermediateStopsLabel =
    intermediateStopCount === 1 ? 'Intermediate stop' : 'Intermediate stops';

  const resolvedStops = useMemo(
    () => (carrier.stops?.length ? carrier.stops : stops || []),
    [carrier.stops, stops]
  );

  useEffect(() => {
    const nextStopFee = stopFee ?? 0;
    setStopFeeInputValue(String(nextStopFee));

    if (nextStopFee > 0 && defaultStopFee === 0) {
      setDefaultStopFee(nextStopFee);
      setIsDefaultStopFee(true);
      return;
    }

    setIsDefaultStopFee(nextStopFee === defaultStopFee);
  }, [stopFee, defaultStopFee]);

  const handleUpdateDefaultStopFee = async (newFee: string) => {
    const feeValue = Number(newFee);
    if (isNaN(feeValue) || feeValue < 0) {
      toast({
        description: 'Please enter a valid stop fee.',
        variant: 'destructive',
      });
      return false;
    }

    const resp = await updateUserDefaultStopFee(feeValue);

    if (resp.isErr()) {
      toast({
        description: 'Failed to update default stop fee.',
        variant: 'destructive',
      });
      return false;
    }

    setDefaultStopFee(feeValue);
    setIsDefaultStopFee(true);
    setStopFeeInputValue(String(feeValue));
    setStopFee?.(feeValue);

    return true;
  };

  const handleChangeStopFee = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '' || /^\d+$/.test(value)) {
      setStopFeeInputValue(value);
      const feeValue = value === '' ? 0 : parseInt(value, 10);

      if (!isNaN(feeValue)) {
        setStopFee?.(feeValue);
        setIsDefaultStopFee(feeValue === defaultStopFee);
      }
    }
  };

  const handleStopFeeFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === '0') {
      e.target.select();
    }
  };

  const handleStopFeeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const feeValue = value === '' ? 0 : parseInt(value, 10);

    if (!isNaN(feeValue)) {
      setStopFeeInputValue(String(feeValue));
      setStopFee?.(feeValue);
      setIsDefaultStopFee(feeValue === defaultStopFee);
    }
  };

  const fetchMarketInformation = useCallback(async () => {
    const datIntegration = quotingIntegrations.find(
      (integration) => integration.name === Quoting.DAT
    );

    const isAnyMarketInformationEnabled =
      isDATMarketConditionsEnabled ||
      isDATMarketConditionsForecastEnabled ||
      isDATLoadToTruckRatioEnabled;

    if (
      !isAnyMarketInformationEnabled ||
      !isDATRateView ||
      !datIntegration ||
      resolvedStops.length === 0
    ) {
      return;
    }

    // Create a new abort controller for this fetch
    abortControllerRef.current = new AbortController();
    const abortSignal = abortControllerRef.current.signal;

    setIsMarketInformationLoading(true);

    try {
      const result = await getDATMarketInformation({
        quickQuoteId: carrier.quoteId,
        integrationID: datIntegration?.id,
        transportType: carrier.actualTransportType,
        stops: resolvedStops,
      });

      // Only update state if this request hasn't been aborted
      if (!abortSignal.aborted) {
        if (result.isOk()) {
          setMarketInformationData({
            pickupMarketInformation: result.value.pickupMarketInformation,
            dropoffMarketInformation: result.value.dropoffMarketInformation,
          });
        }
        // Mark that we've attempted to fetch with this set of dependencies
        hasAttemptedFetchRef.current = true;
      }
    } catch (error) {
      // Only log error if this request hasn't been aborted
      if (!abortSignal.aborted) {
        console.error('Failed to fetch market conditions:', error);
        // Mark that we've attempted to fetch with this set of dependencies
        hasAttemptedFetchRef.current = true;
      }
    } finally {
      // Only update loading state if this request hasn't been aborted
      if (!abortSignal.aborted) {
        setIsMarketInformationLoading(false);
      }
    }
  }, [
    isDATMarketConditionsEnabled,
    isDATMarketConditionsForecastEnabled,
    isDATLoadToTruckRatioEnabled,
    isDATRateView,
    quotingIntegrations,
    carrier.actualTransportType,
    carrier.quoteId,
    resolvedStops,
  ]);

  const handleMarketInformationToggle = async () => {
    setIsMarketInformationExpanded(!isMarketInformationExpanded);

    if (
      isMarketInformationExpanded ||
      isMarketInformationLoading ||
      !!marketInformationData
    ) {
      return;
    }

    fetchMarketInformation();
  };

  useEffect(() => {
    // Cancel any in-flight request when dependencies change
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Reset the attempt flag and clear old data when dependencies change, allowing retry with new params
    hasAttemptedFetchRef.current = false;
    setMarketInformationData(undefined);
  }, [
    resolvedStops,
    carrier.actualTransportType,
    carrier.quoteId,
    isDATRateView,
  ]);

  useEffect(() => {
    if (
      isMarketInformationExpanded &&
      !isMarketInformationLoading &&
      !marketInformationData &&
      !hasAttemptedFetchRef.current
    ) {
      fetchMarketInformation();
    }
  }, [
    fetchMarketInformation,
    isMarketInformationExpanded,
    isMarketInformationLoading,
    marketInformationData,
  ]);

  // Cleanup: cancel any in-flight requests on component unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Use fetched data if available, otherwise fall back to carrier data
  const pickupMarketInformation =
    marketInformationData?.pickupMarketInformation;
  const dropoffMarketInformation =
    marketInformationData?.dropoffMarketInformation;
  const orderedStops = useMemo(() => {
    if (!resolvedStops || resolvedStops.length === 0) {
      return [];
    }

    return [...resolvedStops].sort((a, b) => a.order - b.order);
  }, [resolvedStops]);
  const pickupStop = orderedStops[0];
  const dropoffStop = orderedStops[orderedStops.length - 1];
  const pickupMarketLocation = getMarketLocationLabel(
    pickupMarketInformation,
    pickupStop
  );
  const dropoffMarketLocation = getMarketLocationLabel(
    dropoffMarketInformation,
    dropoffStop
  );

  // Check if this quote should show expandable route leg sections
  const shouldShowExpansion =
    carrier.isMultiStop &&
    carrier.legs &&
    carrier.legs.length > 1 &&
    carrier.type === SelectedQuoteType.DAT_LEG_TO_LEG;

  const initialDATTimeframe =
    (carrier.tooltipContent?.timeframe as DATQuoteTimeframe) || null;
  const initialDATLocationType =
    (carrier.tooltipContent?.originType as DATQuoteLocationType) || null;

  const [isChangeLookupExpanded, setIsChangeLookupExpanded] = useState(false);
  const [selectedDATTimeframe, setSelectedDATTimeframe] =
    useState<Maybe<DATQuoteTimeframe>>(initialDATTimeframe);
  const [selectedDATLocationType, setSelectedDATLocationType] = useState<
    Maybe<DATQuoteLocationType>
  >(initialDATLocationType);

  const handleTargetBuyToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedTargetBuy(!expandedTargetBuy);
  };

  const handlePriceRangeToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedPriceRange(!expandedPriceRange);
  };

  const isGSTriumph =
    carrier.type === SelectedQuoteType.GS_BUYPOWER ||
    carrier.type === SelectedQuoteType.GS_NETWORK;

  const getTargetBuyText = () => {
    if (isTql) {
      return 'Truck Pay';
    }

    if (carrier.type === SelectedQuoteType.GS_NETWORK) {
      return 'Market Rate';
    }

    return 'Buy Rate';
  };

  const handleUpdateRateView = () => {
    if (selectedDATTimeframe && selectedDATLocationType) {
      onUpdateRateView?.(
        selectedDATTimeframe,
        selectedDATLocationType,
        setUpdateRateViewError
      );
    }
  };

  const isDrayage = carrier.actualTransportType === TransportType.DRAYAGE;
  const isUpdateRateViewUnchanged =
    selectedDATTimeframe === initialDATTimeframe &&
    selectedDATLocationType === initialDATLocationType;

  const isDATCard =
    carrier.type === SelectedQuoteType.DAT_RATEVIEW ||
    carrier.type === SelectedQuoteType.DAT_LONGEST_LEG ||
    carrier.type === SelectedQuoteType.DAT_LEG_TO_LEG;

  const tooltipContent = carrier?.tooltipContent;
  const tooltipConstructor = carrier?.tooltipConstructor;

  const shouldShowTooltip =
    tooltipContent &&
    tooltipConstructor &&
    (!isDATCard || hasDATTooltipContent(tooltipContent));

  const popoverRootContainer = getDrumkitContainer();

  const renderMarketAnalyticsContent = () => {
    if (isMarketInformationLoading) {
      return (
        <div className='flex items-center justify-center py-4'>
          <Loader2 className='h-4 w-4 animate-spin mr-2' />
        </div>
      );
    }

    if (pickupMarketInformation && dropoffMarketInformation) {
      return (
        <Tabs defaultValue='pickup' className='w-full'>
          <TabsList className='grid w-full grid-cols-2 mb-3 m-0'>
            <TabsTrigger
              value='pickup'
              className='flex items-center gap-1 rounded-b-none data-[state=inactive]:[&>p]:!inline'
            >
              <MapPin className='h-3 w-3' />
              <Typography variant='body-xs'>Pickup</Typography>
            </TabsTrigger>
            <TabsTrigger
              value='dropoff'
              className='flex items-center gap-1 rounded-b-none data-[state=inactive]:[&>p]:!inline'
            >
              <Navigation className='h-3 w-3' />
              <Typography variant='body-xs'>Dropoff</Typography>
            </TabsTrigger>
          </TabsList>

          <TabsContent value='pickup' className='mt-0'>
            <div className='my-3'>
              <Typography
                variant='body-xs'
                className='text-neutral-500 font-medium truncate'
              >
                Showing data for: {pickupMarketLocation}
              </Typography>
            </div>
            {renderDATMarketAnalytics(pickupMarketInformation)}
          </TabsContent>

          <TabsContent value='dropoff' className='mt-0'>
            <div className='my-3'>
              <Typography
                variant='body-xs'
                className='text-neutral-500 font-medium truncate'
              >
                Showing data for: {dropoffMarketLocation}
              </Typography>
            </div>
            {renderDATMarketAnalytics(dropoffMarketInformation)}
          </TabsContent>
        </Tabs>
      );
    }

    return (
      <div className='text-center py-4'>
        <Typography variant='body-sm' className='text-neutral-500'>
          No market analytics available
        </Typography>
      </div>
    );
  };

  // These early returns must stay below every hook call in this component.
  if (carrier.type === SelectedQuoteType.TRUPAY) {
    return (
      <QuoteCardTruPay
        carrier={carrier}
        isSelected={isSelected}
        onClick={onClick}
      />
    );
  }

  if (carrier.type === SelectedQuoteType.KENCO_FLASH_AVERAGE) {
    return (
      <QuoteCardKenco
        carrier={carrier}
        isSelected={isSelected}
        onClick={onClick}
      />
    );
  }

  if (carrier.type === SelectedQuoteType.SONAR) {
    return (
      <QuoteCardSonar
        carrier={carrier}
        isSelected={isSelected}
        onClick={onClick}
      />
    );
  }

  if (carrier.type === SelectedQuoteType.TRANSFIX) {
    return (
      <QuoteCardTransfix
        carrier={carrier}
        isSelected={isSelected}
        onClick={onClick}
      />
    );
  }

  if (carrier.type === SelectedQuoteType.SUPER_DISPATCH) {
    return (
      <QuoteCardSuperDispatch
        carrier={carrier}
        isSelected={isSelected}
        onClick={onClick}
      />
    );
  }

  return (
    <TooltipProvider>
      <Flex
        key={carrier.type}
        direction='col'
        className={cn(
          'relative w-full border-neutral-400/70 border-2 rounded-[4px] p-2 transition-transform',
          isSelected
            ? 'border-brand-400'
            : 'hover:border-brand-400/60 cursor-pointer hover:scale-[1.01] bg-neutral-50'
        )}
        onClick={onClick}
      >
        <Flex justify='between' align='center' className='w-full mb-2'>
          <Flex direction='col'>
            <Flex align='center' gap='xs'>
              <Typography variant='body-sm' weight='semibold'>
                {carrier.title}
              </Typography>
              {shouldShowTooltip && tooltipContent && tooltipConstructor && (
                <Tooltip delayDuration={10}>
                  <TooltipTrigger onClick={(e: any) => e.preventDefault()}>
                    <InfoIcon
                      className={cn(
                        'h-4 w-4 text-neutral-600',
                        tooltipContent.isMultiStop &&
                          String(carrier.type).includes('LANE_HISTORY') &&
                          'text-error-500'
                      )}
                    />
                  </TooltipTrigger>
                  <TooltipPortal container={popoverRootContainer}>
                    <TooltipContent className='ml-2 max-w-64 whitespace-pre-wrap z-50'>
                      {tooltipConstructor(tooltipContent)}
                    </TooltipContent>
                  </TooltipPortal>
                </Tooltip>
              )}
            </Flex>
            {carrier.subtitle && (
              <span className='text-xs text-neutral-600'>
                {carrier.subtitle}
              </span>
            )}
          </Flex>
          {carrier.icon}
        </Flex>

        <Flex direction='col' className='w-full'>
          <Flex justify='between' align='center' className='w-full mt-2'>
            <Flex align='center' className='gap-x-1.5'>
              <Truck className='h-4 w-4' />
              <Typography variant='body-sm'>
                {isTql ? 'Equipment Type' : 'Transport Type'}
              </Typography>
            </Flex>
            {/* Show the submitted (parent) transport type as the value; if it was proxied or a
                granular equipment type was selected, explain the mapping in a tooltip. */}
            {showEquivalenceTooltip ? (
              <Flex align='baseline' gap='sm'>
                <Tooltip delayDuration={10}>
                  <TooltipTrigger className='border-b border-dashed border-neutral-900'>
                    <Typography variant='body-sm'>{submittedLabel}</Typography>
                  </TooltipTrigger>
                  <TooltipPortal container={popoverRootContainer}>
                    <TooltipContent className='mr-1 max-w-60 whitespace-pre-wrap'>
                      <Typography variant='body-xs'>
                        {getTransportProxyTooltipMessage(
                          carrier.quotingIntegrationName,
                          inputtedEquipmentLabel,
                          submittedLabel,
                          carrier.inputtedTransportType,
                          carrier.actualTransportType
                        )}
                      </Typography>
                    </TooltipContent>
                  </TooltipPortal>
                </Tooltip>
              </Flex>
            ) : (
              <Typography variant='body-sm'>{submittedLabel}</Typography>
            )}
          </Flex>

          <Flex justify='between' align='center' className='w-full'>
            <Flex align='center' gap='sm'>
              <BadgeDollarSignIcon className='h-4 w-4' />
              <Typography variant='body-sm'>{getTargetBuyText()}</Typography>
            </Flex>
            <Flex align='center' className='gap-x-2'>
              <Flex align='baseline' className='gap-x-2'>
                <Typography variant='body-xs'>
                  {formatBuyRate(roundedCost)}
                </Typography>
                {roundedCostPerMile && !isDrayage && (
                  <Typography variant='body-xs'>
                    {`(${formatPerMileRate(roundedCostPerMile, 2)}/mi)`}
                  </Typography>
                )}
              </Flex>
              {shouldShowExpansion && (
                <button
                  onClick={handleTargetBuyToggle}
                  type='button'
                  className='p-0.5 hover:bg-neutral-100 rounded'
                >
                  {expandedTargetBuy ? (
                    <ChevronUpIcon className='h-3 w-3' />
                  ) : (
                    <ChevronDownIcon className='h-3 w-3' />
                  )}
                </button>
              )}
            </Flex>
          </Flex>

          {/* Expanded Target Buy Details */}
          {expandedTargetBuy && shouldShowExpansion && carrier.legs && (
            <QuoteLegBreakdown legs={carrier.legs} type='target' />
          )}

          {showStopFeeEditor && (
            <Flex
              direction='col'
              gap='xs'
              className='w-full mt-3 pt-3 border-t border-neutral-200'
              onClick={(e) => e.stopPropagation()}
            >
              <Typography variant='body-sm'>Stop Fee</Typography>
              <Flex align='center' className='relative w-full'>
                <span className='absolute left-1 text-sm text-neutral-400'>
                  $
                </span>
                <Input
                  type='number'
                  value={stopFeeInputValue}
                  onChange={handleChangeStopFee}
                  onBlur={handleStopFeeBlur}
                  onFocus={handleStopFeeFocus}
                  min={0}
                  className='pl-4 pr-2 py-1.5 text-sm w-full min-w-0'
                  aria-label='Stop Fee'
                />

                <KitDefaultValueTooltip
                  inputValue={stopFeeInputValue}
                  isDefaultValue={isDefaultStopFee}
                  setIsDefaultValue={setIsDefaultStopFee}
                  updateHandler={handleUpdateDefaultStopFee}
                  tooltipTextFormatter={(value: string) =>
                    `Click to save $${value} as default stop fee`
                  }
                  buttonName={ButtonNamePosthog.UpdateDefaultStopFee}
                />
              </Flex>
              <Typography variant='body-xs' className='text-neutral-500'>
                {`${intermediateStopsLabel}: ${intermediateStopCount} x ${formatStopFeeAmount(stopFeeValue)} = ${formatStopFeeAmount(stopFeeTotal)}`}
              </Typography>
            </Flex>
          )}

          <Flex
            justify='between'
            align='center'
            className={cn(
              'w-full',
              showStopFeeEditor &&
                roundedPriceRange &&
                'mt-3 pt-3 border-t border-neutral-200'
            )}
          >
            {roundedPriceRange && (
              <>
                <Flex align='center' gap='sm'>
                  <CoinsIcon className='h-4 w-4' />
                  <Typography variant='body-sm'>Price Range</Typography>
                </Flex>
                <Flex align='center' className='gap-x-2'>
                  {roundedPriceRangePerMile ? (
                    <Tooltip delayDuration={10}>
                      <TooltipTrigger className='border-b border-dashed border-neutral-900'>
                        <Typography variant='body-xs'>
                          {formatRange(
                            roundedPriceRange.lowEstimate,
                            roundedPriceRange.highEstimate
                          )}
                        </Typography>
                      </TooltipTrigger>
                      {/* TooltipPortal allows tooltip to be rendered outside the parent's container's DOM hierarchy
                      to fix the issue where tooltip is hidden by other quote cards, despite high z-index. */}
                      <TooltipPortal container={popoverRootContainer}>
                        <TooltipContent className='mr-2 max-w-60 whitespace-pre-wrap'>
                          <div className='pb-3'>
                            <Typography variant='body-xs'>
                              {`Price range per mile`}
                            </Typography>
                            <Typography variant='body-xs'>
                              {`${formatPerMileRate(roundedPriceRangePerMile.lowEstimate, 2)} - ${formatPerMileRate(roundedPriceRangePerMile.highEstimate, 2)}/mi.`}
                            </Typography>
                          </div>

                          <Typography
                            variant='body-sm'
                            className='border-t pt-3'
                          >
                            All prices include fuel.
                          </Typography>
                        </TooltipContent>
                      </TooltipPortal>
                    </Tooltip>
                  ) : (
                    <Typography variant='body-sm'>
                      {formatRange(
                        roundedPriceRange.lowEstimate,
                        roundedPriceRange.highEstimate
                      )}
                    </Typography>
                  )}
                  {shouldShowExpansion && (
                    <button
                      onClick={handlePriceRangeToggle}
                      type='button'
                      className='p-0.5 hover:bg-neutral-100 rounded'
                    >
                      {expandedPriceRange ? (
                        <ChevronUpIcon className='h-3 w-3' />
                      ) : (
                        <ChevronDownIcon className='h-3 w-3' />
                      )}
                    </button>
                  )}
                </Flex>
              </>
            )}
          </Flex>

          <Flex justify='between' align='center' className='w-full'>
            {roundedConfidence && (
              <>
                <Flex align='center' gap='sm'>
                  <ShieldCheckIcon className='h-4 w-4' />
                  <Typography variant='body-sm'>Confidence</Typography>
                </Flex>
                <QuoteConfidenceLevel
                  confidence={roundedConfidence}
                  lowConfidenceThreshold={lowConfidenceThreshold}
                  mediumConfidenceThreshold={mediumConfidenceThreshold}
                  hidePercentSign={isGSTriumph}
                />
              </>
            )}
          </Flex>

          {/* Expanded Price Range Details */}
          {expandedPriceRange &&
            shouldShowExpansion &&
            carrier.legs &&
            roundedPriceRange && (
              <QuoteLegBreakdown legs={carrier.legs} type='range' />
            )}

          {/* Market Analytics */}
          {isDATRateView &&
            (isDATMarketConditionsEnabled ||
              isDATMarketConditionsForecastEnabled ||
              isDATLoadToTruckRatioEnabled) && (
              <Collapsible
                open={isMarketInformationExpanded}
                onOpenChange={handleMarketInformationToggle}
                className='w-full'
              >
                <CollapsibleTrigger className='w-full cursor-pointer py-2 border-t border-neutral-200 mt-3 pt-3 hover:bg-neutral-100/75'>
                  <Flex align='center' gap='sm' justify='between'>
                    <Typography variant='body-sm' weight='medium'>
                      Market Analytics
                    </Typography>
                    {isMarketInformationExpanded ? (
                      <ChevronUpIcon className='h-4 w-4' />
                    ) : (
                      <ChevronDownIcon className='h-4 w-4' />
                    )}
                  </Flex>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  {renderMarketAnalyticsContent()}
                </CollapsibleContent>
              </Collapsible>
            )}

          {/* Expanded Price Range Details */}
          {isDATRateView && isUpdateRateViewEnabled && (
            <>
              <Button
                buttonNamePosthog={ButtonNamePosthog.QuickQuoteAddStop}
                type='button'
                variant='ghost'
                onClick={() =>
                  setIsChangeLookupExpanded(!isChangeLookupExpanded)
                }
                className='h-8 flex gap-2 items-center text-xs text-neutral-800 px-4 mx-auto mt-4 mb-2 hover:border-neutral-700 hover:bg-neutral-100'
                size='sm'
              >
                Change lookup
                {isChangeLookupExpanded ? (
                  <ChevronUpIcon className='h-3 w-3' />
                ) : (
                  <ChevronDownIcon className='h-3 w-3' />
                )}
              </Button>

              {isChangeLookupExpanded && (
                <Flex direction='col' gap='md' className='w-full'>
                  <Flex direction='col' gap='sm' className='w-full'>
                    {selectedDATTimeframe && (
                      <Flex direction='col' gap='xs' className='w-full'>
                        <Typography variant='body-sm'>
                          Timeframe (days)
                        </Typography>
                        <DATTimeframeSlider
                          value={selectedDATTimeframe}
                          onChange={(value) =>
                            setSelectedDATTimeframe(value as DATQuoteTimeframe)
                          }
                          bestFitOption={initialDATTimeframe}
                        />
                      </Flex>
                    )}

                    {selectedDATLocationType && (
                      <Flex direction='col' gap='xs' className='w-full'>
                        <Typography variant='body-sm'>Area Type</Typography>
                        <DATAreaTypeSlider
                          value={selectedDATLocationType}
                          onChange={(value) =>
                            setSelectedDATLocationType(
                              value as DATQuoteLocationType
                            )
                          }
                          bestFitOption={initialDATLocationType}
                        />
                      </Flex>
                    )}
                  </Flex>

                  {updateRateViewError && (
                    <Typography
                      variant='body-xs'
                      align='center'
                      className='text-error-500'
                    >
                      {updateRateViewError}
                    </Typography>
                  )}

                  <Button
                    buttonNamePosthog={
                      ButtonNamePosthog.QuickQuoteUpdateRateView
                    }
                    variant='default'
                    className='mt-2 h-8 w-full'
                    type='button'
                    onClick={handleUpdateRateView}
                    disabled={isUpdateRateViewUnchanged}
                  >
                    Update RateView
                  </Button>
                </Flex>
              )}
            </>
          )}

          {isSelected && (
            <CircleCheckIcon className='absolute -top-3 -right-2.5 fill-neutral-50 stroke-success-500! w-6 h-6' />
          )}
        </Flex>
      </Flex>
    </TooltipProvider>
  );
};

const parseDATLocationDetails = (
  type: DATQuoteLocationType,
  name: string
): string => {
  switch (type) {
    case DATQuoteLocationType['3_DIGIT_ZIP']:
      return `${name.toUpperCase()}`;
    case DATQuoteLocationType['MARKET_AREA']:
      return `${name.replace(' Mkt', '')} Market Area`;
    case DATQuoteLocationType['EXTENDED_MARKET_AREA']:
      return `${name.replace(' X-Mkt', '')} Extended Market Area`;
    case DATQuoteLocationType['STATE']:
      return `${name} State`;
    case DATQuoteLocationType['REGION']:
      return `${name} Region`;
    case DATQuoteLocationType['COUNTRY']:
      return `${name} Country`;
    default:
      return '';
  }
};

const parseDATTimeframe = (timeframe: DATQuoteTimeframe): string => {
  return timeframe
    .replace('_', ' ') // Remove underscores for readability
    .replace('DAYS', 'DAY') // Replace plural for conciseness (e.g. 30 day timeframe)
    .toLowerCase();
};

const formatStopLocation = (stopLocation: string): string => {
  // Split by comma and trim whitespace
  const parts = stopLocation.split(',').map((part) => part.trim());

  if (parts.length === 2) {
    const city = parts[0];
    const state = parts[1];

    if (city && state) {
      return `${titleCase(city)}, ${state.toUpperCase()}`;
    }

    if (city) {
      return titleCase(city);
    }

    if (state) {
      return state.toUpperCase();
    }

    return '';
  }

  const trimmed = stopLocation.trim();

  if (!trimmed) {
    return '';
  }

  // Fallback to titleCase if format is unexpected
  return titleCase(trimmed);
};

const formatStopLabel = (stop?: QuickQuoteResponseStop): string => {
  if (!stop) {
    return '';
  }

  const city = stop.city?.trim();
  const state = stop.state?.trim();
  const zip = stop.zip?.trim();

  if (city && state) {
    return `${titleCase(city)}, ${state.toUpperCase()}`;
  }

  if (state) {
    return state.toUpperCase();
  }

  if (zip) {
    return zip;
  }

  return '';
};

const getMarketLocationLabel = (
  marketInformation?: DATMarketInformation,
  stop?: QuickQuoteResponseStop
): string => {
  const stopLocation = marketInformation?.stopLocation?.trim();

  if (stopLocation) {
    return formatStopLocation(stopLocation);
  }

  return formatStopLabel(stop) || 'Unknown location';
};

export const GreenscreensTooltipConstructor = (
  isZipCodeLookup: boolean
): JSX.Element => {
  return (
    <>
      <Typography variant='body-sm'>
        {isZipCodeLookup
          ? 'This rate is based on your zip code lookup.'
          : 'This rate is based on your city and state lookup.'}
      </Typography>
    </>
  );
};

export const hasDATTooltipContent = (content: TooltipContentType): boolean => {
  if (content.typeInSource !== QuoteTypeInSource.DAT) {
    return true;
  }

  const hasMileageProvenance =
    content.datMileage != null && content.pcmilerMileage != null;

  return (
    Boolean(content.timeframe?.trim()) ||
    Boolean(content.originName?.trim()) ||
    Boolean(content.destinationName?.trim()) ||
    ((content.reports ?? 0) > 0 && (content.companies ?? 0) > 0) ||
    hasMileageProvenance
  );
};

const DATPCMilerMileageNote = ({
  content,
}: {
  content: TooltipContentType;
}): JSX.Element | null => {
  const datMiles = content.datMileage;
  const pcmilerMiles = content.pcmilerMileage;
  if (datMiles == null || pcmilerMiles == null) {
    return null;
  }

  const roundedDatMiles = _.round(datMiles);
  const roundedPCMilerMiles = _.round(pcmilerMiles);
  const milesMatch = roundedPCMilerMiles === roundedDatMiles;

  let note: JSX.Element;
  if (milesMatch) {
    note = (
      <>
        Linehaul and fuel per mile use <b>{roundedPCMilerMiles}</b> PC*Miler
        miles, which matches DAT&apos;s <b>{roundedDatMiles}</b> mile estimate
        for this lane. The all-in total here should line up with DAT.com for the
        same per-mile rates.
      </>
    );
  } else if (roundedPCMilerMiles > roundedDatMiles) {
    note = (
      <>
        Linehaul and fuel per mile use <b>{roundedPCMilerMiles}</b> PC*Miler
        miles. DAT.com uses <b>{roundedDatMiles}</b> miles for this lane.
        PC*Miler is higher, so at the same DAT per-mile rates the all-in total
        here will be <b>higher</b> than DAT.com shows.
      </>
    );
  } else {
    note = (
      <>
        Linehaul and fuel per mile use <b>{roundedPCMilerMiles}</b> PC*Miler
        miles. DAT.com uses <b>{roundedDatMiles}</b> miles for this lane.
        PC*Miler is lower, so at the same DAT per-mile rates the all-in total
        here will be <b>lower</b> than DAT.com shows.
      </>
    );
  }

  return (
    <Typography variant='body-xs' className='mt-2'>
      {note}
    </Typography>
  );
};

export const DATTooltipConstructor = (
  content: TooltipContentType
): JSX.Element => {
  const intermediateStops = Math.max((content.stops?.length ?? 0) - 2, 0);
  const intermediateStopsLabel =
    intermediateStops === 1 ? 'Intermediate stop' : 'Intermediate stops';
  const stopFee = content.stopFeeUSDMedium ?? 0;
  const showStopFeeBreakdown = intermediateStops > 0;

  return (
    <>
      {/* DAT multi-stop Longest Leg */}
      {content.typeInSource === QuoteTypeInSource.DAT_LONGEST_LEG && (
        <>
          <Typography variant='body-xs' className='mb-2'>
            Longest Leg quote calculated by taking the total distance across all
            legs (leg-to-leg) multiplied by the DAT rate per mile for the
            longest leg plus a{' '}
            {content.stopFeeUSDMedium != null ? (
              <b>{formatCurrency(content.stopFeeUSDMedium, 'USD', 0)} </b>
            ) : (
              ''
            )}
            fee per intermediate stop.
            <br />
            This approach is best for routes with short intermediate legs.
          </Typography>

          {showStopFeeBreakdown && (
            <Typography variant='body-xs' className='mb-2'>
              {`${intermediateStopsLabel}: ${intermediateStops} x ${formatCurrency(stopFee, 'USD', 0)} = ${formatCurrency(stopFee * intermediateStops, 'USD', 0)}`}
            </Typography>
          )}

          <Typography variant='body-xs'>
            First to last stop base rate based on a
            <b>{` ${parseDATTimeframe(content.timeframe as DATQuoteTimeframe)} `}</b>
            timeframe from
            <b>{` ${parseDATLocationDetails(content.originType as DATQuoteLocationType, content.originName || '')} `}</b>
            to
            <b>{` ${parseDATLocationDetails(content.destinationType as DATQuoteLocationType, content.destinationName || '')}`}</b>
          </Typography>
          {(content.reports ?? 0) > 0 && (content.companies ?? 0) > 0 && (
            <Typography variant='body-xs' className='mt-2'>
              Data calculated using <b>{content.reports} reports</b> from{' '}
              <b>{content.companies} companies.</b>
            </Typography>
          )}
        </>
      )}

      {/* DAT multi-stop Leg-to-Leg */}
      {content.typeInSource === QuoteTypeInSource.DAT_LEG_TO_LEG && (
        <>
          <Typography
            variant='body-xs'
            className='mb-2'
            style={{ padding: '6px' }}
          >
            Leg to leg quote calculated by summing each leg's DAT rate. This
            approach combines the point-to-point rates for each leg of the trip.
            The rate is the combined total of each leg summed together.
            <br />
          </Typography>

          {(content.reports ?? 0) > 0 && (content.companies ?? 0) > 0 && (
            <Typography
              variant='body-xs'
              className='mt-2'
              style={{ padding: '6px' }}
            >
              Data calculated using <b>{content.reports} reports</b> from{' '}
              <b>{content.companies} companies.</b>
            </Typography>
          )}
        </>
      )}

      {/* DAT 2-stop RateView */}
      {content.typeInSource === QuoteTypeInSource.DAT && (
        <>
          <Typography variant='body-xs'>
            <b>Broker Spot Rate</b> based on a
            <b>{` ${parseDATTimeframe(content.timeframe as DATQuoteTimeframe)} `}</b>
            timeframe from
            <b>{` ${parseDATLocationDetails(content.originType as DATQuoteLocationType, content.originName || '')} `}</b>
            to
            <b>{` ${parseDATLocationDetails(content.destinationType as DATQuoteLocationType, content.destinationName || '')}`}</b>
          </Typography>
          {(content.reports ?? 0) > 0 && (content.companies ?? 0) > 0 && (
            <Typography variant='body-xs' className='mt-2'>
              Data calculated using <b>{content.reports} reports</b> from{' '}
              <b>{content.companies} companies.</b>
            </Typography>
          )}
        </>
      )}

      <DATPCMilerMileageNote content={content} />
    </>
  );
};

export const LaneHistoryTooltipConstructor = (
  content: TooltipContentType
): JSX.Element => {
  return (
    <>
      <Typography className='text-sm'>
        Rate based on a<b>{` ${content.timeframe} `}</b>
        from {`${laneTierMap[content.originType as LaneTier]}`} to
        {` ${laneTierMap[content.destinationType as LaneTier]}`}.
      </Typography>
      {content.isMultiStop && (
        <Typography className='text-xs text-neutral-600 mt-2'>
          <b>Note:</b> This quote is based on 2-stop data, from the first and
          last stop in the form. Multi-stop TMS lane history is coming soon.
        </Typography>
      )}
    </>
  );
};

const renderDATMarketAnalytics = (data: DATMarketInformation) => (
  <Flex direction='col' gap='md'>
    <Grid
      className='text-xs w-full'
      style={{
        gridTemplateColumns: 'calc(40% - 16px) calc(30% + 8px) calc(30% + 8px)',
      }}
      gap='sm'
    >
      <div />
      <Flex align='center' justify='center' gap='xs' className='-ml-6'>
        <ArrowRight className='h-3 w-3 stroke-success!' />
        <Typography variant='body-xs' weight='medium' align='center'>
          Inbound
        </Typography>
      </Flex>
      <Flex align='center' justify='center' gap='xs' className='-ml-8'>
        <ArrowLeft className='h-3 w-3 stroke-info!' />
        <Typography variant='body-xs' weight='medium' align='center'>
          Outbound
        </Typography>
      </Flex>
    </Grid>

    {data.inboundLoadToTruckRatio || data.outboundLoadToTruckRatio ? (
      <Grid
        className='text-xs w-full'
        style={{
          gridTemplateColumns:
            'calc(40% - 16px) calc(30% + 8px) calc(30% + 8px)',
        }}
        gap='sm'
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className='flex items-center gap-1'>
              <Typography variant='body-xs' textColor='lessMuted'>
                L/T Ratio
              </Typography>
              <InfoIcon className='w-3 h-3 cursor-help' />
            </TooltipTrigger>
            <TooltipContent>
              <Typography variant='body-xs' textColor='lessMuted'>
                Load to Truck Ratio from DAT
              </Typography>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Typography
          variant='body-xs'
          weight='medium'
          align='center'
          className='-ml-4'
        >
          {data.inboundLoadToTruckRatio}
        </Typography>
        <Typography
          variant='body-xs'
          weight='medium'
          align='center'
          className='-ml-6'
        >
          {data.outboundLoadToTruckRatio}
        </Typography>
      </Grid>
    ) : null}

    {data.inboundMarketConditionsIndexPrevDay ||
    data.outboundMarketConditionsIndexPrevDay ? (
      <Grid
        className='text-xs w-full'
        style={{
          gridTemplateColumns:
            'calc(40% - 16px) calc(30% + 8px) calc(30% + 8px)',
        }}
        gap='sm'
      >
        <Typography variant='body-xs' textColor='muted'>
          MCI Yesterday
        </Typography>
        <Typography
          variant='body-xs'
          weight='medium'
          align='center'
          className='-ml-4'
        >
          {data.inboundMarketConditionsIndexPrevDay}
        </Typography>
        <Typography
          variant='body-xs'
          weight='medium'
          align='center'
          className='-ml-6'
        >
          {data.outboundMarketConditionsIndexPrevDay}
        </Typography>
      </Grid>
    ) : null}

    {data.inboundMarketConditionsIndexPrevWeek ||
    data.outboundMarketConditionsIndexPrevWeek ? (
      <Grid
        className='text-xs w-full'
        style={{
          gridTemplateColumns:
            'calc(40% - 16px) calc(30% + 8px) calc(30% + 8px)',
        }}
        gap='sm'
      >
        <Typography variant='body-xs' textColor='muted'>
          MCI Week Avg
        </Typography>
        <Typography
          variant='body-xs'
          weight='medium'
          align='center'
          className='-ml-4'
        >
          {data.inboundMarketConditionsIndexPrevWeek}
        </Typography>
        <Typography
          variant='body-xs'
          weight='medium'
          align='center'
          className='-ml-6'
        >
          {data.outboundMarketConditionsIndexPrevWeek}
        </Typography>
      </Grid>
    ) : null}

    {data.inboundMarketConditionsForecastNextDay ||
    data.outboundMarketConditionsForecastNextDay ? (
      <Grid
        className='text-xs w-full'
        style={{
          gridTemplateColumns:
            'calc(40% - 16px) calc(30% + 8px) calc(30% + 8px)',
        }}
        gap='sm'
      >
        <Typography variant='body-xs' textColor='muted'>
          MCI Tomorrow
        </Typography>
        <Typography
          variant='body-xs'
          weight='medium'
          align='center'
          className='-ml-4'
        >
          {data.inboundMarketConditionsForecastNextDay}
        </Typography>
        <Typography
          variant='body-xs'
          weight='medium'
          align='center'
          className='-ml-6'
        >
          {data.outboundMarketConditionsForecastNextDay}
        </Typography>
      </Grid>
    ) : null}
  </Flex>
);

// New component for expandable leg quote breakdown
export function QuoteLegBreakdown({
  legs,
  type,
  className = '',
  showMileage = false,
}: {
  legs: RouteSegment[];
  type: 'target' | 'range';
  className?: string;
  showMileage?: boolean;
}) {
  const popoverRootContainer = getDrumkitContainer();
  const {
    isEnabled: isQuoteCurrencyEnabled,
    formatAmount,
    formatRange,
    formatPerMileRate,
  } = useQuickQuoteCurrency();

  const formatBuyRate = (amount: number, maxFractionDigits = 0) => {
    if (!isQuoteCurrencyEnabled) {
      return `$${amount}`;
    }

    return formatAmount(amount, maxFractionDigits);
  };

  return (
    <Flex
      direction='col'
      className={cn(
        'w-full pl-4 pr-6 mt-1 text-xs text-neutral-500',
        className
      )}
    >
      {legs.map((leg, index) => {
        let label = `Leg ${leg.order + 1}`;
        if (showMileage) {
          label += ` (${_.round(leg.distanceMiles)} mi)`;
        }
        label += ':';
        if (type === 'target') {
          const legCost = leg.rates?.target ? _.round(leg.rates.target) : null;
          const legCostPerMile = leg.rates?.targetPerMile
            ? _.round(leg.rates.targetPerMile, 2)
            : null;
          return (
            <Flex
              key={index}
              justify='between'
              align='center'
              className='w-full mb-1'
            >
              <Flex align='center' gap='xs'>
                <Typography variant='body-xs' className='text-neutral-500'>
                  {label}
                </Typography>
                <Tooltip delayDuration={10}>
                  <TooltipTrigger
                    onClick={(e: React.MouseEvent<HTMLElement>) =>
                      e.preventDefault()
                    }
                  >
                    <InfoIcon className={'h-4 w-4 text-neutral-600'} />
                  </TooltipTrigger>
                  <TooltipPortal container={popoverRootContainer}>
                    <TooltipContent
                      className='ml-2 max-w-64 rounded-sm whitespace-pre-wrap z-50'
                      style={{ borderRadius: '6px' }}
                    >
                      <Typography variant='body-xs' style={{ padding: '6px' }}>
                        {leg.startCityState} - {leg.endCityState}
                      </Typography>
                    </TooltipContent>
                  </TooltipPortal>
                </Tooltip>
              </Flex>

              <Typography
                variant='body-xs'
                className='text-neutral-500 text-right min-w-[90px]'
              >
                {legCost ? (
                  <>
                    {formatBuyRate(legCost)}
                    {legCostPerMile &&
                      ` (${formatPerMileRate(legCostPerMile, 2)}/mi)`}
                  </>
                ) : (
                  'N/A'
                )}
              </Typography>
            </Flex>
          );
        } else {
          const legLow = leg.rates?.low ? _.round(leg.rates.low) : null;
          const legHigh = leg.rates?.high ? _.round(leg.rates.high) : null;
          return (
            <Flex
              key={index}
              justify='between'
              align='center'
              className='w-full mb-1'
            >
              <Flex align='center' gap='xs'>
                <Typography variant='body-xs' className='text-neutral-500'>
                  {label}
                </Typography>
                <Tooltip delayDuration={10}>
                  <TooltipTrigger
                    onClick={(e: React.MouseEvent<HTMLElement>) =>
                      e.preventDefault()
                    }
                  >
                    <InfoIcon className={'h-4 w-4 text-neutral-600'} />
                  </TooltipTrigger>
                  <TooltipPortal container={popoverRootContainer}>
                    <TooltipContent
                      className='ml-2 max-w-64 rounded-sm whitespace-pre-wrap z-50'
                      style={{ borderRadius: '6px' }}
                    >
                      <Typography variant='body-xs' style={{ padding: '6px' }}>
                        {leg.startCityState} - {leg.endCityState}
                      </Typography>
                    </TooltipContent>
                  </TooltipPortal>
                </Tooltip>
              </Flex>

              <Typography
                variant='body-xs'
                className='text-neutral-500 text-right min-w-[90px]'
              >
                {legLow && legHigh ? formatRange(legLow, legHigh) : 'N/A'}
              </Typography>
            </Flex>
          );
        }
      })}
    </Flex>
  );
}

function getTransportProxyTooltipMessage(
  quotingIntegrationName: Undef<string>,
  inputtedEquipmentLabel: string,
  submittedLabel: string,
  inputtedTransportType: TransportType,
  actualTransportType: TransportType
): string {
  const source =
    quotingIntegrationName === SelectedQuoteType.DAT_RATEVIEW
      ? 'RateView'
      : (quotingIntegrationName ?? 'Quoting integration');

  if (
    inputtedTransportType === TransportType.BOXTRUCK &&
    actualTransportType === TransportType.VAN
  ) {
    return `${source} quotes dry van rates, not box truck. Shown for reference only.`;
  }

  return `${source} does not support ${inputtedEquipmentLabel} quotes, showing equivalent ${submittedLabel} quote.`;
}
